import { useState, useRef, useContext } from 'react';
import {
  TextCollectionFieldType,
  TextCollectionField,
} from '@scale/shared/types/params/textcollection';
import { Tooltip } from 'design-system';
import ResponseTemplatingPopper from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplatingPopper';
import { useChatStepChildIdContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatStepChildIdContext';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import BooleanFieldSection from './FieldSections/BooleanFieldSection';
import NumberFieldSection from './FieldSections/NumberFieldSection';
import ScoreSelectionFieldSection from './FieldSections/ScoreSelectionFieldSection';

interface FormFieldSectionProps {
  field: TextCollectionField | undefined; // Remove undefined.;
  readOnly?: boolean;
  assignmentId?: string;
  setSubmitCodeError?: (field_id: string, error: string) => void;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  readOnlyMessage?: string;
}

export default function FormFieldSection(props: FormFieldSectionProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [tooltipVisible, setTooltipVisible] = useState(false);

  const { childId, metadata } = useChatStepChildIdContext();
  const { previewMode: isChatPreviewMode } = useContext(ChatTaskContext);

  const disableField = props.readOnly;
  const disableTooltipOverride = !tooltipVisible;
  const tooltipIsDisabled = (!disableField && disableTooltipOverride) || isChatPreviewMode;

  const FieldSectionComponent = (
    <>
      {props.field?.type === TextCollectionFieldType.Boolean && (
        <BooleanFieldSection field={props.field} disabled={disableField} />
      )}
      {props.field?.type === TextCollectionFieldType.Number &&
        (props.field.use_slider ? (
          <ScoreSelectionFieldSection field={props.field} disabled={disableField} />
        ) : (
          <NumberFieldSection field={props.field} disabled={disableField} />
        ))}
    </>
  );

  return (
    <div className="flex flex-col whitespace-pre-line p-8" ref={ref}>
      <Tooltip
        disableHoverListener={tooltipIsDisabled}
        disableFocusListener={tooltipIsDisabled}
        disableTouchListener={tooltipIsDisabled}
        placement="top"
        title={
          <div className="max-w-[496px]">
            {props.readOnlyMessage
              ? props.readOnlyMessage
              : 'The answer for this field is already provided.'}
          </div>
        }
        onClose={() => setTooltipVisible(false)}
        onOpen={() => setTooltipVisible(true)}
      >
        {childId ? (
          <div className="flex">
            <div className="grow">{FieldSectionComponent}</div>
            {childId && (
              <ResponseTemplatingPopper
                childId={childId}
                metadata={{
                  ...(metadata ?? {}),
                  fieldId: props.field?.field_id,
                  numberFieldScale:
                    props.field?.type === TextCollectionFieldType.Number &&
                    props.field.min !== undefined &&
                    props.field.max !== undefined
                      ? {
                          min: props.field.min,
                          max: props.field.max,
                        }
                      : undefined,
                }}
                options={{ disablePortal: false }}
              />
            )}
          </div>
        ) : (
          <div>{FieldSectionComponent}</div>
        )}
      </Tooltip>
    </div>
  );
}
